import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Send, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ApplySection = () => {
  const { language, t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    company_name: '',
    project_description: '',
    sector: '',
    stage: '',
    website: '',
    linkedin: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/applications`, formData);
      setIsSubmitted(true);
      toast.success(t.apply.form.success);
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        company_name: '',
        project_description: '',
        sector: '',
        stage: '',
        website: '',
        linkedin: '',
      });
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error(t.apply.form.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const sectors = Object.entries(t.apply.sectors).map(([key, value]) => ({
    value: key,
    label: value,
  }));

  const stages = Object.entries(t.apply.stages).map(([key, value]) => ({
    value: key,
    label: value,
  }));

  return (
    <section
      id="apply"
      className="relative section-padding bg-gradient-to-br from-[#0B4DA2] to-[#093C80] overflow-hidden"
      data-testid="apply-section"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 circuit-pattern opacity-10" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#00A8E8]/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-12 ${language === 'ar' ? 'text-right' : ''}`}>
          <span className="inline-block px-4 py-1.5 bg-white/20 text-white rounded-full text-sm font-semibold mb-4">
            {t.apply.badge}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t.apply.title}
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            {t.apply.subtitle}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-3xl p-8 md:p-10 shadow-2xl">
          {isSubmitted ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                {t.apply.successTitle}
              </h3>
              <p className="text-slate-600 mb-6">
                {t.apply.successBody}
              </p>
              <Button
                onClick={() => setIsSubmitted(false)}
                variant="outline"
                className="border-[#0B4DA2] text-[#0B4DA2] hover:bg-[#0B4DA2] hover:text-white"
              >
                {t.apply.another}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="apply-form">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Full Name */}
                <div className="space-y-2">
                  <Label htmlFor="full_name" className={`text-slate-700 font-medium ${language === 'ar' ? 'text-right block' : ''}`}>
                    {t.apply.form.fullName} *
                  </Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    className="h-12 border-slate-200 focus:border-[#0B4DA2] focus:ring-[#0B4DA2]"
                    data-testid="input-fullname"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className={`text-slate-700 font-medium ${language === 'ar' ? 'text-right block' : ''}`}>
                    {t.apply.form.email} *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="h-12 border-slate-200 focus:border-[#0B4DA2] focus:ring-[#0B4DA2]"
                    data-testid="input-email"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className={`text-slate-700 font-medium ${language === 'ar' ? 'text-right block' : ''}`}>
                    {t.apply.form.phone} *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="h-12 border-slate-200 focus:border-[#0B4DA2] focus:ring-[#0B4DA2]"
                    data-testid="input-phone"
                  />
                </div>

                {/* Company Name */}
                <div className="space-y-2">
                  <Label htmlFor="company_name" className={`text-slate-700 font-medium ${language === 'ar' ? 'text-right block' : ''}`}>
                    {t.apply.form.companyName}
                  </Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    className="h-12 border-slate-200 focus:border-[#0B4DA2] focus:ring-[#0B4DA2]"
                    data-testid="input-company"
                  />
                </div>

                {/* Sector */}
                <div className="space-y-2">
                  <Label className={`text-slate-700 font-medium ${language === 'ar' ? 'text-right block' : ''}`}>
                    {t.apply.form.sector} *
                  </Label>
                  <Select
                    value={formData.sector}
                    onValueChange={(value) => handleSelectChange('sector', value)}
                    required
                  >
                    <SelectTrigger className="h-12 border-slate-200" data-testid="select-sector">
                      <SelectValue placeholder={t.apply.form.sector} />
                    </SelectTrigger>
                    <SelectContent>
                      {sectors.map((sector) => (
                        <SelectItem key={sector.value} value={sector.value}>
                          {sector.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Stage */}
                <div className="space-y-2">
                  <Label className={`text-slate-700 font-medium ${language === 'ar' ? 'text-right block' : ''}`}>
                    {t.apply.form.stage} *
                  </Label>
                  <Select
                    value={formData.stage}
                    onValueChange={(value) => handleSelectChange('stage', value)}
                    required
                  >
                    <SelectTrigger className="h-12 border-slate-200" data-testid="select-stage">
                      <SelectValue placeholder={t.apply.form.stage} />
                    </SelectTrigger>
                    <SelectContent>
                      {stages.map((stage) => (
                        <SelectItem key={stage.value} value={stage.value}>
                          {stage.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Website */}
                <div className="space-y-2">
                  <Label htmlFor="website" className={`text-slate-700 font-medium ${language === 'ar' ? 'text-right block' : ''}`}>
                    {t.apply.form.website}
                  </Label>
                  <Input
                    id="website"
                    name="website"
                    type="url"
                    value={formData.website}
                    onChange={handleChange}
                    className="h-12 border-slate-200 focus:border-[#0B4DA2] focus:ring-[#0B4DA2]"
                    placeholder="https://"
                    data-testid="input-website"
                  />
                </div>

                {/* LinkedIn */}
                <div className="space-y-2">
                  <Label htmlFor="linkedin" className={`text-slate-700 font-medium ${language === 'ar' ? 'text-right block' : ''}`}>
                    {t.apply.form.linkedin}
                  </Label>
                  <Input
                    id="linkedin"
                    name="linkedin"
                    type="url"
                    value={formData.linkedin}
                    onChange={handleChange}
                    className="h-12 border-slate-200 focus:border-[#0B4DA2] focus:ring-[#0B4DA2]"
                    placeholder="https://linkedin.com/in/"
                    data-testid="input-linkedin"
                  />
                </div>
              </div>

              {/* Project Description */}
              <div className="space-y-2">
                <Label htmlFor="project_description" className={`text-slate-700 font-medium ${language === 'ar' ? 'text-right block' : ''}`}>
                  {t.apply.form.projectDescription} *
                </Label>
                <Textarea
                  id="project_description"
                  name="project_description"
                  value={formData.project_description}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="border-slate-200 focus:border-[#0B4DA2] focus:ring-[#0B4DA2] resize-none"
                  data-testid="input-description"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full md:w-auto bg-[#0B4DA2] hover:bg-[#093C80] text-white px-10 py-6 text-lg rounded-full font-semibold transition-all duration-300 hover:shadow-lg disabled:opacity-70 relative z-10"
                data-testid="submit-application-btn"
              >
                {isSubmitting ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    {t.apply.form.submitting}
                  </>
                ) : (
                  <>
                    {t.apply.form.submit}
                    <Send className={`w-5 h-5 ${language === 'ar' ? 'mr-2 rotate-180' : 'ml-2'}`} />
                  </>
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
